// AstriaIndiaV2Wizard.tsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { SHELL_COLORS_INDIA_V2, INDIA_V2_HEADING_FONT } from "./AstriaIndiaV2Shell";

// ─── Astria India v2 — guided question-flow primitives ────────────────────
// Step-by-step wizard: one question per screen, animated progress bar,
// Back/Next controls. Dropdown, Slider, Chips, MultiSelect, Emoji Wheel.
//
// Reused as-is by PH/ID/BR/MX/VN v2 lanes (see PhilippinesV2Tab.tsx /
// IndonesiaV2Tab.tsx / BrazilV2Tab.tsx / MexicoV2Tab.tsx / VietnamV2Tab.tsx)
// since they all share the same single-question field shapes. Components
// that render color take an optional `colors` prop so those lanes can pass
// their own shell palette instead of inheriting India's purple/gold; it
// defaults to SHELL_COLORS_INDIA_V2 so India V2 itself (which doesn't pass
// it) is unaffected. Deliberately a structural subset (not
// `typeof SHELL_COLORS_INDIA_V2`) so each lane's own palette — which has
// its own extra/missing keys — satisfies it without needing every field
// India's happens to have.
interface WizardColors {
  bodyColor: string;
  titleColor: string;
  mutedColor: string;
  accent: string;
  accentSoft: string;
  gold: string;
  section: string;
  cardBorder: string;
  white: string;
}

interface WizardProgressProps {
  step: number; // 1-indexed
  total: number;
  accent?: string;
  stepLabel?: (step: number, total: number) => string;
  percentLabel?: (pct: number) => string;
}

export const WizardProgress: React.FC<WizardProgressProps> = ({
  step,
  total,
  accent = SHELL_COLORS_INDIA_V2.accent,
  stepLabel = (s, t) => `Step ${s} of ${t}`,
  percentLabel = (pct) => `${pct}% complete`,
}) => {
  const pct = Math.round((step / total) * 100);
  return (
    <div style={{ marginBottom: 28 }}>
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 8, fontSize: 12, color: SHELL_COLORS_INDIA_V2.mutedColor }}
      >
        <span>{stepLabel(step, total)}</span>
        <span>{percentLabel(pct)}</span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 999,
          background: SHELL_COLORS_INDIA_V2.section,
          overflow: "hidden",
        }}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{
            height: "100%",
            borderRadius: 999,
            background: `linear-gradient(90deg, ${accent}, ${SHELL_COLORS_INDIA_V2.gold})`,
          }}
        />
      </div>
    </div>
  );
};

interface WizardStepProps {
  stepKey: string; // used as the animation key so steps swap with a slide/fade
  question: string;
  helper?: string;
  children: React.ReactNode;
}

export const WizardStep: React.FC<WizardStepProps> = ({
  stepKey,
  question,
  helper,
  children,
}) => (
  <AnimatePresence mode="wait">
    <motion.div
      key={stepKey}
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -16 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
    >
      <h2
        style={{
          fontSize: 21,
          fontWeight: 700,
          color: SHELL_COLORS_INDIA_V2.titleColor,
          fontFamily: INDIA_V2_HEADING_FONT,
          marginBottom: helper ? 6 : 20,
          lineHeight: 1.35,
        }}
      >
        {question}
      </h2>
      {helper && (
        <p
          style={{
            fontSize: 13.5,
            color: SHELL_COLORS_INDIA_V2.mutedColor,
            marginBottom: 20,
            lineHeight: 1.6,
          }}
        >
          {helper}
        </p>
      )}
      {children}
    </motion.div>
  </AnimatePresence>
);

interface WizardNavProps {
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
  backLabel?: string;
  nextDisabled?: boolean;
  showBack?: boolean;
}

export const WizardNav: React.FC<WizardNavProps> = ({
  onBack,
  onNext,
  nextLabel = "Continue",
  backLabel = "Back",
  nextDisabled = false,
  showBack = true,
}) => (
  <div className="flex items-center gap-3" style={{ marginTop: 28 }}>
    {showBack && onBack && (
      <button
        onClick={onBack}
        className="flex items-center gap-1 rounded-full text-sm font-semibold transition-all hover:bg-black/[0.03]"
        style={{
          paddingInline: 18,
          paddingBlock: 12,
          color: SHELL_COLORS_INDIA_V2.bodyColor,
          border: `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
        }}
      >
        <ChevronLeft className="w-4 h-4" />
        {backLabel}
      </button>
    )}
    <button
      onClick={onNext}
      disabled={nextDisabled}
      className="flex items-center justify-center gap-1.5 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
      style={{
        flex: 1,
        paddingBlock: 13,
        background: `linear-gradient(135deg, ${SHELL_COLORS_INDIA_V2.accent} 0%, ${SHELL_COLORS_INDIA_V2.gold} 100%)`,
        boxShadow: `0 6px 18px ${SHELL_COLORS_INDIA_V2.accentSoft}`,
      }}
    >
      {nextLabel}
      <ChevronRight className="w-4 h-4" />
    </button>
  </div>
);

// ─── Dropdown ───────────────────────────────────────────────────────────
interface DropdownOption {
  value: string;
  label: string;
}

export const WizardDropdown: React.FC<{
  options: DropdownOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ options, value, onChange, placeholder = "Select an option" }) => {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between rounded-2xl text-left transition-all"
        style={{
          padding: "16px 18px",
          background: SHELL_COLORS_INDIA_V2.white,
          border: `1.5px solid ${open ? SHELL_COLORS_INDIA_V2.accent : SHELL_COLORS_INDIA_V2.cardBorder}`,
          boxShadow: open ? SHELL_COLORS_INDIA_V2.accentGlow : "none",
        }}
      >
        <span
          style={{
            fontSize: 15,
            color: selected ? SHELL_COLORS_INDIA_V2.titleColor : SHELL_COLORS_INDIA_V2.mutedColor,
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className="w-4 h-4"
          style={{
            color: SHELL_COLORS_INDIA_V2.mutedColor,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s ease",
          }}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              right: 0,
              zIndex: 10,
              background: SHELL_COLORS_INDIA_V2.white,
              border: `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
              borderRadius: 16,
              boxShadow: "0 12px 32px rgba(46,31,69,0.14)",
              overflow: "hidden",
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className="w-full flex items-center justify-between text-left transition-colors hover:bg-black/[0.02]"
                style={{
                  padding: "13px 18px",
                  fontSize: 14.5,
                  color: SHELL_COLORS_INDIA_V2.bodyColor,
                  background:
                    opt.value === value ? SHELL_COLORS_INDIA_V2.section : "transparent",
                }}
              >
                {opt.label}
                {opt.value === value && (
                  <Check className="w-4 h-4" style={{ color: SHELL_COLORS_INDIA_V2.accent }} />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Slider ─────────────────────────────────────────────────────────────
export const WizardSlider: React.FC<{
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  lowLabel?: string;
  highLabel?: string;
  colors?: WizardColors;
}> = ({ value, onChange, min = 0, max = 100, lowLabel, highLabel, colors = SHELL_COLORS_INDIA_V2 }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div
        style={{
          textAlign: "center",
          fontSize: 34,
          fontWeight: 700,
          color: colors.accent,
          fontFamily: INDIA_V2_HEADING_FONT,
          marginBottom: 14,
        }}
      >
        {value}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: "100%",
          height: 8,
          borderRadius: 999,
          appearance: "none",
          background: `linear-gradient(90deg, ${colors.accent} ${pct}%, ${colors.section} ${pct}%)`,
          outline: "none",
          cursor: "pointer",
          accentColor: colors.accent,
        }}
      />
      {(lowLabel || highLabel) && (
        <div
          className="flex items-center justify-between"
          style={{ marginTop: 8, fontSize: 12, color: colors.mutedColor }}
        >
          <span>{lowLabel}</span>
          <span>{highLabel}</span>
        </div>
      )}
    </div>
  );
};

// ─── Choice chips (single-select) ────────────────────────────────────────
export const WizardChips: React.FC<{
  options: DropdownOption[];
  value: string | null;
  onChange: (value: string) => void;
  colors?: WizardColors;
}> = ({ options, value, onChange, colors = SHELL_COLORS_INDIA_V2 }) => (
  <div className="flex flex-wrap gap-2.5">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className="rounded-full text-sm font-medium transition-all"
          style={{
            paddingInline: 18,
            paddingBlock: 11,
            color: active ? "#fff" : colors.bodyColor,
            background: active
              ? `linear-gradient(135deg, ${colors.accent} 0%, ${colors.gold} 100%)`
              : colors.section,
            border: active ? "none" : `1px solid ${colors.cardBorder}`,
            boxShadow: active ? `0 4px 14px ${colors.accentSoft}` : "none",
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

// ─── Multi-select chips ──────────────────────────────────────────────────
export const WizardMultiSelect: React.FC<{
  options: DropdownOption[];
  values: string[];
  onChange: (values: string[]) => void;
}> = ({ options, values, onChange }) => {
  const toggle = (v: string) => {
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  };
  return (
    <div className="flex flex-wrap gap-2.5">
      {options.map((opt) => {
        const active = values.includes(opt.value);
        return (
          <button
            key={opt.value}
            onClick={() => toggle(opt.value)}
            className="flex items-center gap-1.5 rounded-full text-sm font-medium transition-all"
            style={{
              paddingInline: 16,
              paddingBlock: 11,
              color: active ? "#fff" : SHELL_COLORS_INDIA_V2.bodyColor,
              background: active
                ? `linear-gradient(135deg, ${SHELL_COLORS_INDIA_V2.accent} 0%, ${SHELL_COLORS_INDIA_V2.gold} 100%)`
                : SHELL_COLORS_INDIA_V2.section,
              border: active ? "none" : `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
              boxShadow: active ? `0 4px 14px ${SHELL_COLORS_INDIA_V2.accentSoft}` : "none",
            }}
          >
            {active && <Check className="w-3.5 h-3.5" />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

// ─── Emoji wheel ──────────────────────────────────────────────────────────
const EMOJI_OPTIONS: { emoji: string; label: string; value: string }[] = [
  { emoji: "😊", label: "Content", value: "content" },
  { emoji: "😔", label: "Low", value: "low" },
  { emoji: "😟", label: "Anxious", value: "anxious" },
  { emoji: "😡", label: "Frustrated", value: "frustrated" },
  { emoji: "😌", label: "Calm", value: "calm" },
  { emoji: "😢", label: "Sad", value: "sad" },
  { emoji: "🥱", label: "Drained", value: "drained" },
  { emoji: "🤔", label: "Confused", value: "confused" },
];

export const WizardEmojiWheel: React.FC<{
  value: string | null;
  onChange: (value: string) => void;
  labels?: Record<string, string>;
  colors?: WizardColors;
}> = ({ value, onChange, labels, colors = SHELL_COLORS_INDIA_V2 }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 12,
    }}
  >
    {EMOJI_OPTIONS.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className="flex flex-col items-center gap-1.5 rounded-2xl transition-all"
          style={{
            padding: "16px 8px",
            background: active ? colors.accentSoft : colors.section,
            border: active
              ? `1.5px solid ${colors.accent}`
              : `1px solid ${colors.cardBorder}`,
            transform: active ? "scale(1.05)" : "scale(1)",
          }}
        >
          <span style={{ fontSize: 28 }}>{opt.emoji}</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: active ? colors.accent : colors.mutedColor,
            }}
          >
            {labels?.[opt.value] || opt.label}
          </span>
        </button>
      );
    })}
  </div>
);

// ─── Mind / Heart / Both selector (Bhavna Drishti "where it sits") ───────
export const WizardTriChoice: React.FC<{
  options: DropdownOption[];
  value: string | null;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => (
  <div className="grid grid-cols-3 gap-2.5">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className="rounded-2xl text-sm font-semibold transition-all"
          style={{
            paddingBlock: 18,
            color: active ? "#fff" : SHELL_COLORS_INDIA_V2.bodyColor,
            background: active
              ? `linear-gradient(135deg, ${SHELL_COLORS_INDIA_V2.accent} 0%, ${SHELL_COLORS_INDIA_V2.gold} 100%)`
              : SHELL_COLORS_INDIA_V2.section,
            border: active ? "none" : `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

export const WizardTextInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  colors?: WizardColors;
}> = ({ value, onChange, placeholder, multiline = false, colors = SHELL_COLORS_INDIA_V2 }) => {
  const sharedStyle: React.CSSProperties = {
    width: "100%",
    padding: "16px 18px",
    fontSize: 15,
    borderRadius: 16,
    border: `1.5px solid ${colors.cardBorder}`,
    background: colors.white,
    color: colors.titleColor,
    outline: "none",
    fontFamily: "inherit",
  };
  return multiline ? (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={4}
      style={{ ...sharedStyle, resize: "vertical" }}
    />
  ) : (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={sharedStyle}
    />
  );
};
